import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { useApp } from '../context/AppContext';
import Layout from '../components/layout/Layout';
import Header from '../components/layout/Header';
import KPICard from '../components/dashboard/KPICard';
import { Skeleton } from '../components/ui/skeleton';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { 
  Building2, 
  Shield, 
  Megaphone, 
  DollarSign, 
  Users,
  AlertTriangle,
  TrendingUp,
  Eye,
  Download,
  FileText,
  Target,
  Mountain,
  Star,
  Compass,
  Heart,
  CheckCircle2
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BoardView = () => {
  const { selectedYear, periodType, selectedPeriod } = useApp();
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [scorecard, setScorecard] = useState(null);
  const [actionItems, setActionItems] = useState([]);
  const [foundations, setFoundations] = useState([]);
  const [rocks, setRocks] = useState([]);
  const [pillars, setPillars] = useState([]);
  const reportRef = useRef(null);

  const currentQuarter = `Q${Math.ceil((new Date().getMonth() + 1) / 3)}`;

  useEffect(() => {
    fetchData();
  }, [selectedYear, periodType, selectedPeriod]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Build params - include period_start and period_end for non-ytd/yearly periods
      const params = { 
        year: selectedYear, 
        period_type: periodType 
      };
      
      // Add period dates for weekly, monthly, quarterly views
      if (selectedPeriod && ['weekly', 'monthly', 'quarterly'].includes(periodType)) {
        params.period_start = selectedPeriod.startDate;
        params.period_end = selectedPeriod.endDate;
      }
      
      const [scorecardRes, actionsRes, foundationsRes, rocksRes, pillarsRes] = await Promise.all([
        axios.get(`${API}/dashboard/board/view`, { params }),
        axios.get(`${API}/action-items`),
        axios.get(`${API}/strategy/foundation`).catch(() => ({ data: [] })),
        axios.get(`${API}/strategy/rocks`, { params: { year: selectedYear, quarter: currentQuarter } }).catch(() => ({ data: [] })),
        axios.get(`${API}/strategy/pillars`).catch(() => ({ data: [] }))
      ]);
      setScorecard(scorecardRes.data);
      setActionItems(actionsRes.data.filter(a => a.status !== 'complete').slice(0, 5));
      setFoundations(foundationsRes.data);
      setRocks(rocksRes.data);
      setPillars(pillarsRes.data);
    } catch (error) {
      console.error('Failed to fetch board view data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getRAGStatus = (metric) => {
    // Use status from backend if available
    if (metric?.rag_status) return metric.rag_status;
    
    // Fallback calculation
    if (!metric?.has_data && metric?.has_data !== undefined) return 'no_data';
    if (!metric?.plan_value) return 'no_plan';
    const variance = (metric.actual_value - metric.plan_value) / Math.abs(metric.plan_value);
    const isLowerBetter = metric.directionality === 'lower_is_better';
    const adjustedVariance = isLowerBetter ? -variance : variance;
    
    if (adjustedVariance >= 0) return 'green';
    if (adjustedVariance >= -0.05) return 'yellow';
    return 'red';
  };

  const functionConfig = {
    brokerage: { 
      icon: Building2, 
      label: 'Brokerage',
      headline: ['Premium Written', 'Revenue', 'Retention Rate']
    },
    mga: { 
      icon: Shield, 
      label: 'MGA',
      headline: ['Gross Written Premium', 'Loss Ratio', 'Capacity Utilization']
    },
    marketing: { 
      icon: Megaphone, 
      label: 'Marketing',
      headline: ['Pipeline Sourced', 'CAC', 'MQLs Generated']
    },
    finance: { 
      icon: DollarSign, 
      label: 'Finance',
      headline: ['Cash Balance', 'Runway Months', 'EBITDA Margin']
    },
    people: { 
      icon: Users, 
      label: 'People',
      headline: ['Headcount', 'Attrition Rate', 'Time to Fill']
    }
  };

  const exportToPDF = async () => {
    setExporting(true);
    toast.info('Generating PDF report...');
    
    try {
      const doc = new jsPDF('p', 'mm', 'a4');
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 15;
      let yPos = margin;

      // Load logo from local public folder (avoids CORS)
      const logoUrl = '/ph_logo_lockup.png';
      
      // Fetch and convert to base64
      let logoBase64 = null;
      try {
        const response = await fetch(logoUrl);
        const blob = await response.blob();
        logoBase64 = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(blob);
        });
      } catch (e) {
        console.warn('Could not load logo for PDF:', e);
      }

      // Header
      doc.setFillColor(15, 23, 42); // slate-900
      doc.rect(0, 0, pageWidth, 40, 'F');
      
      // Add logo if loaded (wide lockup logo)
      if (logoBase64) {
        const logoHeight = 16;
        const logoWidth = 50; // Wide logo with "Players Health" text
        doc.addImage(logoBase64, 'PNG', margin, 4, logoWidth, logoHeight);
      } else {
        // Fallback text if logo doesn't load
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(22);
        doc.setFont('helvetica', 'bold');
        doc.text('Players Health Pulse', margin, 18);
      }
      
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      doc.text(`${selectedYear} ${periodType.charAt(0).toUpperCase() + periodType.slice(1)} Performance Summary`, margin, 28);
      
      doc.setFontSize(9);
      doc.text(`Generated: ${new Date().toLocaleDateString('en-US', { 
        weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' 
      })}`, margin, 35);

      yPos = 50;

      // Executive Summary
      doc.setTextColor(15, 23, 42);
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('Executive Summary', margin, yPos);
      yPos += 8;

      // Summary stats
      let totalMetrics = 0, greenCount = 0, yellowCount = 0, redCount = 0, noDataCount = 0;
      if (scorecard?.scorecard) {
        Object.values(scorecard.scorecard).forEach(func => {
          // Use summary if available
          if (func.summary) {
            totalMetrics += func.summary.total_definitions || func.metrics.length;
            greenCount += func.summary.green || 0;
            yellowCount += func.summary.yellow || 0;
            redCount += func.summary.red || 0;
            noDataCount += func.summary.no_data || 0;
          } else {
            func.metrics.forEach(metric => {
              totalMetrics++;
              const status = getRAGStatus(metric);
              if (status === 'green') greenCount++;
              else if (status === 'yellow') yellowCount++;
              else if (status === 'red') redCount++;
              else if (status === 'no_data') noDataCount++;
            });
          }
        });
      }

      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(71, 85, 105); // slate-600
      const summaryText = noDataCount > 0 
        ? `Total KPIs: ${totalMetrics}  |  On Track: ${greenCount}  |  At Risk: ${yellowCount}  |  Off Track: ${redCount}  |  No Data: ${noDataCount}`
        : `Total KPIs: ${totalMetrics}  |  On Track: ${greenCount}  |  At Risk: ${yellowCount}  |  Off Track: ${redCount}`;
      doc.text(summaryText, margin, yPos);
      yPos += 12;

      // Function sections
      if (scorecard?.scorecard) {
        Object.entries(scorecard.scorecard).forEach(([func, data]) => {
          if (yPos > pageHeight - 60) {
            doc.addPage();
            yPos = margin;
          }

          const config = functionConfig[func];
          
          // Section header
          doc.setFillColor(241, 245, 249); // slate-100
          doc.rect(margin, yPos - 4, pageWidth - (margin * 2), 8, 'F');
          
          doc.setTextColor(15, 23, 42);
          doc.setFontSize(12);
          doc.setFont('helvetica', 'bold');
          doc.text(config?.label || func.toUpperCase(), margin + 2, yPos + 2);
          yPos += 10;

          // Metrics table
          const headlineMetrics = config?.headline || [];
          const metricsToShow = data.metrics.filter(m => 
            headlineMetrics.includes(m.metric_name)
          ).slice(0, 4);
          
          const displayMetrics = metricsToShow.length > 0 ? metricsToShow : data.metrics.slice(0, 4);
          
          const tableData = displayMetrics.map(metric => {
            const status = getRAGStatus(metric);
            const variance = metric.plan_value 
              ? `${((metric.actual_value - metric.plan_value) / Math.abs(metric.plan_value) * 100).toFixed(1)}%`
              : 'N/A';
            
            return [
              metric.metric_name,
              `${metric.actual_value} ${metric.unit}`,
              metric.plan_value ? `${metric.plan_value} ${metric.unit}` : 'N/A',
              variance,
              status.toUpperCase()
            ];
          });

          autoTable(doc, {
            startY: yPos,
            head: [['Metric', 'Actual', 'Plan', 'Variance', 'Status']],
            body: tableData,
            margin: { left: margin, right: margin },
            styles: { fontSize: 9, cellPadding: 2 },
            headStyles: { fillColor: [37, 99, 235], textColor: 255 },
            columnStyles: {
              0: { cellWidth: 50 },
              4: { cellWidth: 20, halign: 'center' }
            },
            didParseCell: function(data) {
              if (data.column.index === 4 && data.section === 'body') {
                const status = data.cell.raw;
                if (status === 'GREEN') {
                  data.cell.styles.textColor = [16, 185, 129];
                  data.cell.styles.fontStyle = 'bold';
                } else if (status === 'YELLOW') {
                  data.cell.styles.textColor = [245, 158, 11];
                  data.cell.styles.fontStyle = 'bold';
                } else if (status === 'RED') {
                  data.cell.styles.textColor = [244, 63, 94];
                  data.cell.styles.fontStyle = 'bold';
                }
              }
            }
          });

          yPos = doc.lastAutoTable?.finalY + 10 || yPos + 30;
        });
      }

      // Action Items section
      if (actionItems.length > 0) {
        if (yPos > pageHeight - 60) {
          doc.addPage();
          yPos = margin;
        }

        doc.setFillColor(241, 245, 249);
        doc.rect(margin, yPos - 4, pageWidth - (margin * 2), 8, 'F');
        
        doc.setTextColor(15, 23, 42);
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text('Key Action Items', margin + 2, yPos + 2);
        yPos += 10;

        const actionData = actionItems.map(action => [
          action.description,
          action.owner,
          new Date(action.due_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          action.status.replace('_', ' ').toUpperCase()
        ]);

        autoTable(doc, {
          startY: yPos,
          head: [['Action', 'Owner', 'Due', 'Status']],
          body: actionData,
          margin: { left: margin, right: margin },
          styles: { fontSize: 9, cellPadding: 2 },
          headStyles: { fillColor: [37, 99, 235], textColor: 255 },
          columnStyles: {
            0: { cellWidth: 90 },
            3: { cellWidth: 25, halign: 'center' }
          }
        });
      }

      // Footer on each page
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(148, 163, 184);
        doc.text(
          `Players Health Pulse  |  Confidential  |  Page ${i} of ${pageCount}`,
          pageWidth / 2,
          pageHeight - 10,
          { align: 'center' }
        );
      }

      // Save the PDF
      doc.save(`players-health-pulse-${selectedYear}-${periodType}.pdf`);
      toast.success('PDF exported successfully!');
    } catch (error) {
      console.error('PDF export error:', error);
      toast.error('Failed to export PDF');
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <Header title="Board View" subtitle="Read-only summary for Board and Investors" />
        <div className="p-6 space-y-6">
          <div className="grid grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Skeleton key={i} className="h-48 rounded-lg" />
            ))}
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <Header title="Board View" subtitle="Read-only summary for Board and Investors" />
      
      <div className="p-6 space-y-8" data-testid="board-view" ref={reportRef}>
        {/* Board Header */}
        <div className="bg-slate-900 rounded-xl p-8 text-white">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <Eye className="w-6 h-6 text-slate-400" />
                <span className="text-sm font-medium text-slate-400 uppercase tracking-wider">
                  {selectedYear} • {selectedPeriod?.label || (periodType.charAt(0).toUpperCase() + periodType.slice(1))} Review
                </span>
              </div>
              <h1 className="text-3xl font-bold font-[Manrope] mb-2">
                Players Health Performance Summary
              </h1>
              <p className="text-slate-400">
                Executive KPI Dashboard for Board of Directors and Investors
              </p>
            </div>
            <Button 
              onClick={exportToPDF} 
              disabled={exporting}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="export-pdf-btn"
            >
              {exporting ? (
                <>
                  <FileText className="w-4 h-4 mr-2 animate-pulse" />
                  Exporting...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  Export PDF
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Strategic Foundation (SVEP) Section */}
        {foundations.length > 0 && foundations.some(f => f.title) && (
          <div className="bg-white rounded-lg border border-slate-200 p-6">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <Target className="w-5 h-5 text-amber-600" />
              </div>
              <h2 className="text-lg font-semibold text-slate-900 font-[Manrope]">
                Strategic Foundation
              </h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {foundations.filter(f => f.title).map((foundation) => {
                const iconMap = {
                  pinnacle: { icon: Star, color: 'text-amber-500', bg: 'bg-amber-50' },
                  mission: { icon: Compass, color: 'text-blue-500', bg: 'bg-blue-50' },
                  purpose: { icon: Heart, color: 'text-rose-500', bg: 'bg-rose-50' },
                  core_business: { icon: Building2, color: 'text-emerald-500', bg: 'bg-emerald-50' }
                };
                const config = iconMap[foundation.type] || iconMap.pinnacle;
                const Icon = config.icon;
                const labelMap = {
                  pinnacle: 'Pinnacle (BHAG)',
                  mission: 'Mission',
                  purpose: 'Purpose',
                  core_business: 'Core Business'
                };
                
                return (
                  <div key={foundation.type} className={`${config.bg} rounded-lg p-4`}>
                    <div className="flex items-start gap-3">
                      <Icon className={`w-5 h-5 ${config.color} mt-0.5 flex-shrink-0`} />
                      <div>
                        <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">
                          {labelMap[foundation.type]}
                        </p>
                        <p className="font-semibold text-slate-900">{foundation.title}</p>
                        {foundation.description && (
                          <p className="text-sm text-slate-600 mt-1 line-clamp-2">{foundation.description}</p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Quarterly Rocks Section */}
        {rocks.length > 0 && (
          <div className="bg-white rounded-lg border border-slate-200 p-6">
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <Mountain className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-slate-900 font-[Manrope]">
                    {currentQuarter} {selectedYear} Rocks
                  </h2>
                  <p className="text-sm text-slate-500">Quarterly execution priorities</p>
                </div>
              </div>
              <div className="flex items-center gap-4 text-sm">
                <div className="flex items-center gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-full bg-green-500"></div>
                  <span className="text-slate-600">{rocks.filter(r => r.status === 'green').length} On Track</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-full bg-yellow-500"></div>
                  <span className="text-slate-600">{rocks.filter(r => r.status === 'yellow').length} At Risk</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-full bg-red-500"></div>
                  <span className="text-slate-600">{rocks.filter(r => r.status === 'red').length} Off Track</span>
                </div>
              </div>
            </div>
            
            <div className="space-y-3">
              {rocks.map((rock) => {
                const statusColors = {
                  not_started: { bg: 'bg-slate-100', text: 'text-slate-600', dot: 'bg-slate-400' },
                  green: { bg: 'bg-green-50', text: 'text-green-700', dot: 'bg-green-500' },
                  yellow: { bg: 'bg-yellow-50', text: 'text-yellow-700', dot: 'bg-yellow-500' },
                  red: { bg: 'bg-red-50', text: 'text-red-700', dot: 'bg-red-500' },
                  completed: { bg: 'bg-blue-50', text: 'text-blue-700', dot: 'bg-blue-500' }
                };
                const statusConfig = statusColors[rock.status] || statusColors.not_started;
                const statusLabels = {
                  not_started: 'Not Started',
                  green: 'On Track',
                  yellow: 'At Risk',
                  red: 'Off Track',
                  completed: 'Completed'
                };
                
                return (
                  <div 
                    key={rock.id} 
                    className={`${statusConfig.bg} rounded-lg p-4 border-l-4`}
                    style={{ borderLeftColor: statusConfig.dot.replace('bg-', '').includes('slate') ? '#94a3b8' : undefined }}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <div className={`w-2 h-2 rounded-full ${statusConfig.dot}`}></div>
                          <h3 className="font-medium text-slate-900">{rock.name}</h3>
                        </div>
                        <div className="flex items-center gap-3 text-sm text-slate-500">
                          <span>{rock.owner_name}</span>
                          <span 
                            className="px-2 py-0.5 rounded text-xs"
                            style={{ backgroundColor: rock.pillar_color + '20', color: rock.pillar_color }}
                          >
                            {rock.pillar_name}
                          </span>
                        </div>
                        {rock.description && (
                          <p className="text-sm text-slate-600 mt-2 line-clamp-1">{rock.description}</p>
                        )}
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className="flex items-center gap-2">
                          <div className="w-24">
                            <Progress value={rock.percent_complete} className="h-2" />
                          </div>
                          <span className="text-sm font-medium text-slate-700 w-10 text-right">
                            {rock.percent_complete}%
                          </span>
                        </div>
                        <p className={`text-xs mt-1 ${statusConfig.text}`}>
                          {statusLabels[rock.status]}
                        </p>
                      </div>
                    </div>
                    {rock.risks_blockers && (
                      <div className="flex items-center gap-2 mt-2 text-sm text-red-600">
                        <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
                        <span className="line-clamp-1">{rock.risks_blockers}</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Function Summaries */}
        <div className="space-y-6">
          {scorecard?.scorecard && Object.entries(scorecard.scorecard).map(([func, data]) => {
            const config = functionConfig[func];
            const Icon = config?.icon || Building2;
            
            // Show all board-visible metrics (already filtered by backend)
            const displayMetrics = data.metrics;

            // Skip sections with no metrics
            if (displayMetrics.length === 0) return null;

            return (
              <div key={func} className="bg-white rounded-lg border border-slate-200 p-6">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                    <Icon className="w-5 h-5 text-slate-600" />
                  </div>
                  <h2 className="text-lg font-semibold text-slate-900 font-[Manrope]">
                    {config?.label || func}
                  </h2>
                  <span className="text-xs text-slate-400 bg-slate-100 px-2 py-1 rounded-full">
                    {displayMetrics.length} KPIs
                  </span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 items-start">
                  {displayMetrics.map((metric) => (
                    <KPICard
                      key={metric.metric_name}
                      title={metric.metric_name}
                      value={metric.actual_value}
                      unit={metric.unit}
                      planValue={metric.plan_value}
                      variance={metric.plan_value ? (metric.actual_value - metric.plan_value) / Math.abs(metric.plan_value) : null}
                      ragStatus={getRAGStatus(metric)}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* Key Risks & Open Actions */}
        <div className="grid grid-cols-2 gap-6">
          {/* Metrics At Risk */}
          <div className="bg-white rounded-lg border border-slate-200 p-6">
            <div className="flex items-center gap-2 mb-4">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              <h3 className="text-sm font-medium text-slate-500 uppercase tracking-wider">
                Metrics Requiring Attention
              </h3>
            </div>
            <div className="space-y-3">
              {scorecard?.scorecard && Object.entries(scorecard.scorecard).flatMap(([func, data]) => 
                data.metrics
                  .filter(m => getRAGStatus(m) === 'red' || getRAGStatus(m) === 'yellow')
                  .map(m => ({ ...m, function: func }))
              ).slice(0, 5).map((metric, idx) => (
                <div key={idx} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                  <div>
                    <p className="text-sm font-medium text-slate-700">{metric.metric_name}</p>
                    <p className="text-xs text-slate-400 capitalize">{metric.function}</p>
                  </div>
                  <div className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                    getRAGStatus(metric) === 'red' 
                      ? 'bg-rose-100 text-rose-700'
                      : 'bg-amber-100 text-amber-700'
                  }`}>
                    {metric.plan_value 
                      ? `${((metric.actual_value - metric.plan_value) / Math.abs(metric.plan_value) * 100).toFixed(1)}% vs plan`
                      : 'No plan'
                    }
                  </div>
                </div>
              ))}
              {(!scorecard?.scorecard || Object.values(scorecard.scorecard).every(d => 
                d.metrics.every(m => getRAGStatus(m) === 'green' || getRAGStatus(m) === 'no_plan')
              )) && (
                <div className="text-center py-6 text-slate-400">
                  <TrendingUp className="w-8 h-8 mx-auto mb-2 text-emerald-400" />
                  <p>All metrics on track</p>
                </div>
              )}
            </div>
          </div>

          {/* Open Action Items */}
          <div className="bg-white rounded-lg border border-slate-200 p-6">
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp className="w-5 h-5 text-blue-500" />
              <h3 className="text-sm font-medium text-slate-500 uppercase tracking-wider">
                Key Actions In Progress
              </h3>
            </div>
            <div className="space-y-3">
              {actionItems.length > 0 ? actionItems.map((action) => (
                <div key={action.id} className="flex items-start justify-between py-2 border-b border-slate-100 last:border-0">
                  <div>
                    <p className="text-sm text-slate-700">{action.description}</p>
                    <p className="text-xs text-slate-400">
                      {action.owner} • Due {new Date(action.due_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </p>
                  </div>
                  <span className={`px-2.5 py-1 rounded-full text-xs font-medium capitalize ${
                    action.status === 'in_progress' 
                      ? 'bg-amber-100 text-amber-700'
                      : 'bg-blue-100 text-blue-700'
                  }`}>
                    {action.status.replace('_', ' ')}
                  </span>
                </div>
              )) : (
                <div className="text-center py-6 text-slate-400">
                  <p>No open action items</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center text-sm text-slate-400 pt-4 border-t border-slate-200">
          <p>Generated {new Date().toLocaleDateString('en-US', { 
            weekday: 'long',
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
          })}</p>
        </div>
      </div>
    </Layout>
  );
};

export default BoardView;
